// FILE: src/services/sessionService.js
//
// Supabase persistence for completed exam sessions.
// The session object and per-question states live in App.js React state;
// this service provides a fire-and-forget write to the exam_sessions table
// when a session ends so results are durable across page reloads.
//
// Phase 1 analytics (migration 011) extends this with a follow-up insert
// into question_attempts — one row per question — so the Admin → Question
// Performance view can aggregate per-question metrics over time.  That
// follow-up is fire-and-forget too: if it fails we log a warning but
// surface no error to the learner.

import { supabase } from './supabaseClient';
import { getBaselineColumn, getActiveCertIds } from '../config/exams';
import { computeResults } from '../components/session/ResultsPage';
import { persistQuestionAttempts } from './questionAttemptsService';
import { recordReadinessSnapshot, setLearnerBaseline } from './readinessService';
import { fireAchievement, sessionAchievementsToFire, ACHIEVEMENT } from './achievementService';
import { resolveWriteOwner } from './authIdentity';
import { trackEvent } from './telemetryService';

// ── Baseline qualification ──────────────────────────────────────────────────
// Canonical minimum: a session must have at least this many ANSWERED
// (correct + incorrect) questions to auto-set a learner's baseline for the
// cert. Decision 2026-08-13 (Journey Activation gate): short sampler
// sessions — e.g. the 5-question journey micro-practice — must NOT establish
// a baseline; a baseline is a calibration measurement, not a participation
// marker. Skipped/unanswered questions don't count. Recalibration via the
// dashboard is unaffected (learner-initiated, LearnerDashboard.handleRecalibrate).
export const MIN_BASELINE_ANSWERED = 10;

/**
 * Persists a completed exam session to the Supabase exam_sessions table,
 * then fires off a batch insert of per-question attempts.
 * Fire-and-forget — errors are logged but never thrown.
 *
 * @param {object}  session           - session object from App.js state
 * @param {object}  questionStates    - { [index]: { selectedAnswers, submitted, timeSpentSeconds } }
 * @param {Set}     skippedIndices    - indices of skipped questions
 * @param {string}  completionReason  - 'completed' | 'early' | 'expired' | 'abandoned'
 *   'abandoned' is written when the learner leaves an in-progress session
 *   (migration 106 keeps those rows out of the learner-facing view).
 * @param {Set}     [flaggedIndices]  - indices the learner flagged; defaults to empty
 * @param {object}  [options]
 * @param {number|null} [options.timeUsedSeconds] - canonical exam-clock seconds
 *        consumed (allowance minus the countdown's remaining seconds; breaks are
 *        excluded because the countdown is paused during them). NOT the allowance:
 *        duration_minutes is the allowance and must never be shown as time used.
 * @returns {Promise<string|null>} the saved session id (null for guests or when
 *        the write did not happen) — used to give the results screen a permanent
 *        address (?view=results&session=<id>).
 */
/**
 * Should an in-progress session be persisted as abandoned before it is thrown
 * away?
 *
 * PM Pixel used to write a session only when it ENDED, so every exit path that
 * called setSession(null) destroyed the learner's answers. This is the guard on
 * the replacement write, kept pure so the rule is testable without mounting the
 * app.
 *
 * Three conditions, all required:
 *   • there is a live session       — nothing to save otherwise
 *   • it has not already persisted  — the completion path owns the write once
 *                                     it has run; never write the same session
 *                                     twice under two different reasons
 *   • the learner answered something — an untouched deck is not practice, and
 *                                     saving one would turn "opened it and
 *                                     left" into a row that looks like work
 *
 * @param {object|null} live             { session, questionStates } or null
 * @param {boolean}     alreadyPersisted value of the session-persisted guard
 * @returns {boolean}
 */
export function shouldPersistAbandonedSession(live, alreadyPersisted) {
  if (!live || !live.session || alreadyPersisted) return false;
  return countAnsweredStates(live.questionStates) > 0;
}

/**
 * How many question slots the learner actually submitted an answer for.
 * Mirrors computeResults' notion of "answered" (a submitted state), so the
 * abandon guard and the persisted answered_count cannot disagree.
 *
 * @param {object} questionStates { [index]: { submitted, ... } }
 * @returns {number}
 */
export function countAnsweredStates(questionStates) {
  if (!questionStates || typeof questionStates !== 'object') return 0;
  return Object.values(questionStates).filter((qs) => qs && qs.submitted).length;
}

export async function persistExamSession(
  session,
  questionStates,
  skippedIndices,
  completionReason,
  flaggedIndices = new Set(),
  { timeUsedSeconds = null } = {},
) {
  try {
    const results = computeResults(session.questions, questionStates, skippedIndices);

    // Build a compact per-question summary for storage (omit full choice text to
    // keep the JSONB payload small — referenceId is sufficient for later lookup).
    const questionSummary = results.items.map((item) => ({
      id:          item.question.id,
      referenceId: item.question.referenceId,
      status:      item.status,        // 'correct' | 'incorrect' | 'skipped' | 'unanswered'
      selected:    item.selectedAnswers || [],
      correct:     item.question.correctAnswers,
    }));

    // Tag the session origin so dashboard metrics can exclude automated test
    // runs. CI sets REACT_APP_SESSION_SOURCE='test' on the build step; real
    // Netlify builds leave it unset, which falls back to 'user'.
    const sessionSource = process.env.REACT_APP_SESSION_SOURCE === 'test' ? 'test' : 'user';

    // Resolve ownership from the ESTABLISHED auth state (in-memory session
    // mirror seeded from the local getSession read) — never a save-time network
    // call. On an unresolved auth state we do NOT silently attribute the row to
    // guest; we record telemetry and rely on the migration-057 BEFORE INSERT
    // trigger to set user_id = auth.uid() from the JWT. Intentional guests still
    // resolve to { userId: null, isGuest: true }. See authIdentity.js.
    let userId = null;
    try {
      const owner = await resolveWriteOwner();
      userId = owner.userId;
    } catch (err) {
      // AttributionError or anything unexpected: auth state unknown. Keep the
      // learner unblocked (the DB trigger attributes authenticated inserts) but
      // never claim this was an intentional guest.
      try {
        trackEvent('attribution_failure', 'technical', {
          stage: 'exam_session',
          reason: (err && err.message) ? String(err.message).slice(0, 120) : 'unknown',
        });
      } catch (_) { /* telemetry never blocks */ }
      userId = null;
    }

    const endedAtIso = new Date().toISOString();

    const row = {
      exam_id:           session.examId,
      mode_name:         session.mode,
      display_name:      session.displayName || null,
      started_at:        new Date(session.startedAt).toISOString(),
      ended_at:          endedAtIso,
      total_questions:   results.total,
      duration_minutes:  session.durationMinutes || null,
      answered_count:    results.correct + results.incorrect,
      score_correct:     results.correct,
      score_incorrect:   results.incorrect,
      score_skipped:     results.skipped,
      score_percentage:  results.percentage,
      completion_reason: completionReason,
      question_summary:  questionSummary,
      source:            sessionSource,
      user_id:           userId,
      time_used_seconds: (typeof timeUsedSeconds === 'number' && timeUsedSeconds >= 0)
        ? Math.round(timeUsedSeconds)
        : null,
    };

    // Use .select().single() so Supabase returns the inserted row — we need the
    // generated uuid to tie question_attempts rows to the parent session.
    let { data, error } = await supabase
      .from('exam_sessions')
      .insert(row)
      .select()
      .single();

    // Ordering guard: if this build reaches an environment where migration 097
    // has not been applied yet, the unknown column would cost the learner their
    // whole session. Retry once without it rather than losing the result.
    if (error && /time_used_seconds/i.test(error.message || '')) {
      console.warn('[sessionService] time_used_seconds not available; retrying without it');
      const { time_used_seconds: _omit, ...legacyRow } = row;
      ({ data, error } = await supabase
        .from('exam_sessions')
        .insert(legacyRow)
        .select()
        .single());
    }

    if (error) {
      console.warn('[sessionService] Supabase insert failed:', error.message);
      return null;
    }

    console.info(
      '[sessionService] Session persisted:',
      session.examId,
      session.mode,
      `${results.percentage}%`,
      `${results.correct}/${results.total}`,
    );

    // Phase 1 analytics: write one question_attempts row per question.
    // Skip silently when the parent insert did not return an id (e.g. when the
    // auto-mock in unit tests short-circuits insert().select().single() to
    // null).  This keeps existing tests passing while still exercising the
    // main persistExamSession code path.
    const sessionId = data?.id || null;
    if (sessionId) {
      await persistQuestionAttempts({
        session,
        sessionId,
        questionStates,
        skippedIndices,
        flaggedIndices,
        source:     sessionSource,
        answeredAt: endedAtIso,
        userId,
      });
    }

    // ── Phase C: baseline auto-set + readiness snapshot ───────────────────
    // Only for signed-in learners on a qualifying session: 'user' source,
    // active cert, and >= MIN_BASELINE_ANSWERED answered questions (see the
    // constant's comment — samplers never set baselines). Errors are logged,
    // never thrown — analytics never blocks the results screen.
    if (
      userId &&
      sessionSource === 'user' &&
      getActiveCertIds().includes(session.examId) &&
      (results.correct + results.incorrect) >= MIN_BASELINE_ANSWERED
    ) {
      try {
        // 1. Detect whether this is the first session for the cert. We check
        //    BEFORE auto-set so we know whether to fire the Phase D event
        //    that triggers the BaselineConfidence overlay AND the
        //    baseline_set achievement (Phase E1).
        const baselineBefore = await readBaselineAt(userId, session.examId);

        // 2. Auto-set the baseline if not already set. The RPC is idempotent
        //    for the auto-set path — we only call it when it's safe to
        //    overwrite. To avoid stomping a recalibrated baseline, we only
        //    set when the profile's baseline_at_<cert> is currently NULL.
        await setBaselineIfUnset(userId, session.examId);

        // 3. Compute readiness from canonical data (refetch from the view)
        //    and write a snapshot via the SECURITY DEFINER RPC. The service
        //    handles the cert filter + post-baseline window itself.
        const baselineAt = await readBaselineAt(userId, session.examId);
        const snapshotResult = await recordReadinessSnapshot({ examId: session.examId, baselineAt });

        // 4. Phase E1 — fire the baseline_set achievement on first-baseline.
        //    The RPC is idempotent, so a no-op if already earned.
        if (!baselineBefore) {
          await fireAchievement({
            key: ACHIEVEMENT.BASELINE_SET,
            examId: null,
            context: { exam_id: session.examId, session_id: sessionId },
          });
        }

        // 5. Phase E1 — fire session-driven achievements (first_full_sim,
        //    three_sims). Decision is pure; count comes from the canonical
        //    exam_sessions table including this just-persisted session.
        const fullSimsToDate = await countFullSimsForUser(userId, session.examId);
        const sessAchs = sessionAchievementsToFire({
          session, results, fullSimsToDate,
        });
        for (const a of sessAchs) {
          await fireAchievement(a);
        }

        // 6. Phase D — when a brand new baseline was just set (i.e. there
        //    was no prior baseline), dispatch the baseline-set event so the
        //    BaselineConfidence overlay can render. Listened to by
        //    HeaderUserMenu so App.js stays untouched.
        if (!baselineBefore && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          try {
            const ev = new CustomEvent('pm-pixel:baseline-set', {
              detail: {
                examId:   session.examId,
                readiness: snapshotResult?.readiness || null,
              },
            });
            window.dispatchEvent(ev);
          } catch (_) { /* CustomEvent unavailable — non-fatal */ }
        }
      } catch (readinessErr) {
        console.warn('[sessionService] readiness snapshot failed (non-fatal):', readinessErr?.message || readinessErr);
      }
    }
    return sessionId;
  } catch (err) {
    console.warn('[sessionService] Unexpected Supabase error:', err);
    return null;
  }
}

// ── Baseline helpers (kept local — light wrappers over the profile table) ──

async function readBaselineAt(userId, examId) {
  const col = getBaselineColumn(examId);
  try {
    const { data, error } = await supabase
      .from('learner_profiles')
      .select(col)
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return null;
    return data?.[col] || null;
  } catch (_) {
    return null;
  }
}

async function setBaselineIfUnset(userId, examId) {
  const current = await readBaselineAt(userId, examId);
  if (current) return; // already set — don't stomp a recalibrated value
  await setLearnerBaseline(examId);
}

// ── Phase E1 — count full sims for the cert (achievement-firing input) ─────
async function countFullSimsForUser(userId, examId) {
  try {
    const { count, error } = await supabase
      .from('exam_sessions')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('exam_id', examId)
      .eq('mode_name', 'full')
      .eq('source', 'user');
    if (error) {
      console.warn('[sessionService] countFullSimsForUser failed:', error.message);
      return 0;
    }
    return typeof count === 'number' ? count : 0;
  } catch (_) {
    return 0;
  }
}
